'use client';

import { produce } from 'immer';
import { create } from 'zustand';

import { useExploreGenerationSettingsStore } from './exploreGenerationSettingsStore';
import { materialsApi, type UserMaterial } from '@/lib/api/materials';
import { getUserFacingErrorMessage } from '@/lib/errors/user-facing-error';
import { appendExploreImageReferenceToken, remapExploreImageReferenceTokens } from '@/lib/explore-image-reference-tokens';
import { getPreferredImageUrl } from '@/lib/image-utils';
import { translateClient } from '@/lib/i18n/client';
import type { AttachedNodeRef, ImageModel } from '@/types/chat';
import { getImageModelSpec } from '@/types/chat';
import type { CustomStyle, SelectedCustomStyle } from '@/types/custom-style';
import type {
  SelectedSubjectModel,
  SelectedSubjectModels,
  SubjectModel,
  SubjectModelType,
} from '@/types/subject-model';

export interface ExploreImageComposerSource {
  id?: string;
  url: string;
  thumbnail_url?: string | null;
  description?: string | null;
  width?: number;
  height?: number;
  referenceEvidence?: AttachedNodeRef['referenceEvidence'];
}

export interface ExploreImageComposerBoundImage {
  token: string;
  source: 'upload' | 'library' | 'explore';
  previewUrl: string;
  description?: string;
  attachment: AttachedNodeRef;
}

export type ExploreComposerUploadResult =
  | { ok: true }
  | { ok: false; code: 'reference-limit-exceeded'; max: number }
  | { ok: false; code: 'upload-failed'; message?: string };

export type ExploreComposerBindResult =
  | { ok: true }
  | { ok: false; code: 'reference-limit-exceeded'; max: number };

interface ExploreImageComposerState {
  boundImages: ExploreImageComposerBoundImage[];
  selectedCustomStyle: SelectedCustomStyle | null;
  selectedSubjectModels: SelectedSubjectModels;
  draft: string;
  isUploading: boolean;
  error: string | null;
  openEmpty: () => void;
  openFromExploreImage: (image: ExploreImageComposerSource) => void;
  setDraft: (draft: string) => void;
  uploadReferenceImages: (
    files: File[],
    capabilitySlotKeys?: Array<AttachedNodeRef['capabilitySlotKey']>,
  ) => Promise<ExploreComposerUploadResult>;
  addReferenceMaterials: (materials: UserMaterial[]) => ExploreComposerBindResult;
  addReferenceSourceImages: (images: ExploreImageComposerSource[]) => ExploreComposerBindResult;
  removeBoundImage: (token: string) => void;
  clearBoundImages: () => void;
  insertBoundImageReference: (token: string) => void;
  setSelectedCustomStyle: (style: CustomStyle | SelectedCustomStyle | null) => void;
  clearSelectedCustomStyle: () => void;
  setSelectedSubjectModel: (
    modelType: SubjectModelType,
    model: SubjectModel | SelectedSubjectModel | null,
  ) => void;
  clearSelectedSubjectModel: (modelType: SubjectModelType) => void;
  clearError: () => void;
  reset: () => void;
}

const emptySubjectModels = (): SelectedSubjectModels => ({ product: null, character: null });

const initialState = () => ({
  boundImages: [] as ExploreImageComposerBoundImage[],
  selectedCustomStyle: null as SelectedCustomStyle | null,
  selectedSubjectModels: emptySubjectModels(),
  draft: '',
  isUploading: false,
  error: null as string | null,
});

function buildToken(index: number): string {
  return `image${index}`;
}

function normalizeSubjectModel(
  modelType: SubjectModelType,
  model: SubjectModel | SelectedSubjectModel,
): SelectedSubjectModel {
  return {
    id: model.id,
    model_type: model.model_type || modelType,
    name: model.name,
    cover_url: model.cover_url,
    anchor_count: 'anchor_count' in model
      ? model.anchor_count
      : ('anchor_images' in model ? model.anchor_images.length : undefined),
    reference_count: 'reference_count' in model
      ? model.reference_count
      : ('reference_urls' in model ? model.reference_urls?.length : undefined),
    reference_urls: 'reference_urls' in model ? model.reference_urls : undefined,
    subject_profile: model.subject_profile,
    quality_warnings: model.quality_warnings,
    analysis_status: 'analysis_status' in model ? model.analysis_status : undefined,
  };
}

function selectedSubjectReferenceCount(models: SelectedSubjectModels): number {
  return (models.product?.reference_count ?? models.product?.reference_urls?.length ?? models.product?.anchor_count ?? 0)
    + (models.character?.reference_count ?? models.character?.reference_urls?.length ?? models.character?.anchor_count ?? 0);
}

export function maxBoundImages(
  style: SelectedCustomStyle | null,
  imageModel: ImageModel,
  subjectModels: SelectedSubjectModels,
): number {
  const styleReferences = style ? (style.style_reference_count ?? 1) : 0;
  return Math.max(
    0,
    getImageModelSpec(imageModel).maxReferenceImages
      - styleReferences
      - selectedSubjectReferenceCount(subjectModels),
  );
}

function reindex(images: ExploreImageComposerBoundImage[]): ExploreImageComposerBoundImage[] {
  return images.map((image, index) => {
    const token = buildToken(index + 1);
    return {
      ...image,
      token,
      attachment: { ...image.attachment, label: token },
    };
  });
}

function tokenRemap(
  previous: ExploreImageComposerBoundImage[],
  remaining: ExploreImageComposerBoundImage[],
  reindexed: ExploreImageComposerBoundImage[],
): Record<string, string | null> {
  return Object.fromEntries(previous.map((image) => {
    const nextIndex = remaining.findIndex((candidate) => candidate.token === image.token);
    return [image.token, nextIndex >= 0 ? reindexed[nextIndex].token : null];
  }));
}

function fromSource(
  image: ExploreImageComposerSource,
  index: number,
  source: ExploreImageComposerBoundImage['source'],
): ExploreImageComposerBoundImage {
  const token = buildToken(index);
  const previewUrl = getPreferredImageUrl(image.url, image.thumbnail_url);
  return {
    token,
    source,
    previewUrl,
    description: image.description ?? undefined,
    attachment: {
      nodeId: image.id || `explore-image-${crypto.randomUUID()}`,
      mediaType: 'image',
      attachmentSource: 'asset',
      mediaUrl: image.url,
      thumbnail: previewUrl,
      label: token,
      referenceEvidence: image.referenceEvidence,
    },
  };
}

function fromMaterial(
  material: UserMaterial,
  index: number,
  source: 'upload' | 'library',
): ExploreImageComposerBoundImage {
  const token = buildToken(index);
  const previewUrl = getPreferredImageUrl(material.url || '', material.thumbnail_url);
  return {
    token,
    source,
    previewUrl,
    attachment: {
      nodeId: material.id || `uploaded-image-${crypto.randomUUID()}`,
      assetId: material.id,
      mediaType: 'image',
      attachmentSource: 'asset',
      mediaUrl: material.url || '',
      thumbnail: previewUrl,
      label: token,
    },
  };
}

function normalizedStyle(style: CustomStyle | SelectedCustomStyle): SelectedCustomStyle {
  return {
    id: style.id,
    style_version: style.style_version,
    style_definition_kind: style.style_definition_kind,
    readiness: style.readiness,
    generation_defaults: style.generation_defaults,
    generation_settings: style.generation_settings,
    conversation_generation_settings: style.conversation_generation_settings,
    preset_id: 'preset_id' in style ? style.preset_id : undefined,
    source: 'source' in style ? style.source : undefined,
    is_system: 'is_system' in style ? style.is_system : undefined,
    name: style.name,
    name_en: 'name_en' in style ? style.name_en : undefined,
    name_zh: 'name_zh' in style ? style.name_zh : undefined,
    cover_url: style.cover_url,
    anchor_count: 'anchor_count' in style
      ? style.anchor_count
      : ('anchor_images' in style ? style.anchor_images.length : undefined),
    style_sheet_url: 'style_sheet_url' in style ? style.style_sheet_url : undefined,
    style_reference_count: 'style_reference_count' in style
      ? style.style_reference_count
      : (('anchor_count' in style && style.anchor_count)
        || ('anchor_images' in style && style.anchor_images.length)
        ? 1
        : undefined),
    style_recipe: style.style_recipe,
    style_tags: style.style_tags,
    analysis_status: 'analysis_status' in style ? style.analysis_status : undefined,
  };
}

export const useExploreImageComposerStore = create<ExploreImageComposerState>((set, get) => ({
  ...initialState(),

  openEmpty: () => set(initialState()),

  openFromExploreImage: (image) => set({
    ...initialState(),
    boundImages: [fromSource(image, 1, 'explore')],
  }),

  setDraft: (draft) => set({ draft }),

  uploadReferenceImages: async (files, capabilitySlotKeys) => {
    const entries = files
      .map((file, index) => ({ file, capabilitySlotKey: capabilitySlotKeys?.[index] }))
      .filter(({ file }) => file.size > 0);
    if (entries.length === 0) return { ok: true };

    const beforeUpload = get();
    const imageModel = useExploreGenerationSettingsStore.getState().imageModel;
    const limit = maxBoundImages(
      beforeUpload.selectedCustomStyle,
      imageModel,
      beforeUpload.selectedSubjectModels,
    );
    if (beforeUpload.boundImages.length + entries.length > limit) {
      return { ok: false, code: 'reference-limit-exceeded', max: limit };
    }

    set({ isUploading: true, error: null });
    try {
      const uploaded = await Promise.all(entries.map(async ({ file }) => {
        const result = await materialsApi.uploadMaterial(file, 'general', {
          displayName: file.name || 'reference-image',
          assetCategory: 'project_asset',
        });
        if (result.error || !result.data?.url) {
          throw result.error ?? new Error(translateClient('explore', 'imageCreateUploadFailed'));
        }
        return result.data;
      }));

      const current = get();
      const currentLimit = maxBoundImages(
        current.selectedCustomStyle,
        useExploreGenerationSettingsStore.getState().imageModel,
        current.selectedSubjectModels,
      );
      if (current.boundImages.length + uploaded.length > currentLimit) {
        set({ isUploading: false });
        return { ok: false, code: 'reference-limit-exceeded', max: currentLimit };
      }

      const nextImages = uploaded.map((material, index) => {
        const image = fromMaterial(material, current.boundImages.length + index + 1, 'upload');
        const capabilitySlotKey = entries[index]?.capabilitySlotKey;
        return capabilitySlotKey
          ? { ...image, attachment: { ...image.attachment, capabilitySlotKey } }
          : image;
      });
      set({
        boundImages: reindex([...current.boundImages, ...nextImages]),
        isUploading: false,
        error: null,
      });
      return { ok: true };
    } catch (error) {
      const message = getUserFacingErrorMessage(
        error,
        'upload',
        translateClient('explore', 'imageCreateUploadFailed'),
      );
      set({ isUploading: false, error: message });
      return { ok: false, code: 'upload-failed', message };
    }
  },

  addReferenceMaterials: (materials) => {
    const usable = materials.filter((material) => material.file_type === 'image' && Boolean(material.url));
    if (usable.length === 0) return { ok: true };
    const state = get();
    const limit = maxBoundImages(
      state.selectedCustomStyle,
      useExploreGenerationSettingsStore.getState().imageModel,
      state.selectedSubjectModels,
    );
    if (state.boundImages.length + usable.length > limit) {
      return { ok: false, code: 'reference-limit-exceeded', max: limit };
    }
    set({
      boundImages: reindex([
        ...state.boundImages,
        ...usable.map((material, index) => (
          fromMaterial(material, state.boundImages.length + index + 1, 'library')
        )),
      ]),
      error: null,
    });
    return { ok: true };
  },

  addReferenceSourceImages: (images) => {
    const usable = images.filter((image) => Boolean(image.url));
    if (usable.length === 0) return { ok: true };
    const state = get();
    const limit = maxBoundImages(
      state.selectedCustomStyle,
      useExploreGenerationSettingsStore.getState().imageModel,
      state.selectedSubjectModels,
    );
    if (state.boundImages.length + usable.length > limit) {
      return { ok: false, code: 'reference-limit-exceeded', max: limit };
    }
    set({
      boundImages: reindex([
        ...state.boundImages,
        ...usable.map((image, index) => (
          fromSource(image, state.boundImages.length + index + 1, 'library')
        )),
      ]),
      error: null,
    });
    return { ok: true };
  },

  removeBoundImage: (token) => set(produce((state: ExploreImageComposerState) => {
    const previous = [...state.boundImages];
    const remaining = previous.filter((image) => image.token.toLowerCase() !== token.toLowerCase());
    if (remaining.length === previous.length) return;
    const reindexed = reindex(remaining);
    state.draft = remapExploreImageReferenceTokens(
      state.draft,
      previous.map((image) => image.token),
      tokenRemap(previous, remaining, reindexed),
    );
    state.boundImages = reindexed;
  })),

  clearBoundImages: () => set(produce((state: ExploreImageComposerState) => {
    const previous = [...state.boundImages];
    state.draft = remapExploreImageReferenceTokens(
      state.draft,
      previous.map((image) => image.token),
      Object.fromEntries(previous.map((image) => [image.token, null])),
    );
    state.boundImages = [];
  })),

  insertBoundImageReference: (token) => set(produce((state: ExploreImageComposerState) => {
    const tokens = state.boundImages.map((image) => image.token);
    if (!tokens.some((candidate) => candidate.toLowerCase() === token.toLowerCase())) return;
    state.draft = appendExploreImageReferenceToken(state.draft, token.toLowerCase(), tokens);
    state.error = null;
  })),

  setSelectedCustomStyle: (style) => set({ selectedCustomStyle: style ? normalizedStyle(style) : null }),
  clearSelectedCustomStyle: () => set({ selectedCustomStyle: null }),
  setSelectedSubjectModel: (modelType, model) => set(produce((state: ExploreImageComposerState) => {
    state.selectedSubjectModels[modelType] = model ? normalizeSubjectModel(modelType, model) : null;
  })),
  clearSelectedSubjectModel: (modelType) => set(produce((state: ExploreImageComposerState) => {
    state.selectedSubjectModels[modelType] = null;
  })),
  clearError: () => set({ error: null }),
  reset: () => set(initialState()),
}));
